import { ButtonDesignTokens } from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = WeaveTheme['tokens'];

const fontFamily = 'Artifakt Element, sans-serif';

/** Fallback small button size values matching the legacy alloy theme */
const SMALL_BUTTON_FALLBACK = {
  height: '24px',
  'font-size': '12px',
  'line-height': '16px',
  'padding-text': '0 7px',
  'padding-icon': '0 3px',
  'padding-icon-text': '0 7px 0 5px',
  'icon-size': 16,
} as const;

/**
 * @param tokens - The base Weave design tokens
 * @param highDensityTokens - high-density tokens used to populate the small button size
 */
export const getButtonTokens = (
  tokens: WeaveTokens,
  highDensityTokens?: WeaveTokens,
): ButtonDesignTokens => {
  const button = tokens?.component?.button;
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;
  const highButton = highDensityTokens?.component?.button;
  const highGeneric = highDensityTokens?.generic;

  const solidBg = button?.solid?.['background-color'];
  const solidText = button?.solid?.['text-color'];
  const solidIcon = button?.solid?.icon?.fill;
  const errorBg = semantic?.['status-color']?.error;

  const solid = {
    ...button?.solid,
    'background-color': solidBg,
    'border-color': button?.solid?.['border-color'],
    'text-color': solidText,
    icon: { fill: solidIcon },
    hover: { 'background-color': solidBg },
    pressed: { 'background-color': solidBg },
    selected: { 'background-color': solidBg },
    disabled: {
      'background-color': solidBg,
      'text-color': solidText,
      icon: { fill: solidIcon },
    },
    loading: { 'background-color': solidBg },
    alert: {
      'background-color': errorBg,
      'text-color': solidText,
      hover: { 'background-color': errorBg },
      pressed: { 'background-color': errorBg },
      loading: { 'background-color': errorBg },
      selected: { 'background-color': errorBg },
    },
  };

  const outlinedBorderColor = button?.outlined?.['border-color'] as
    | { default?: string; pressed?: string }
    | string
    | undefined;
  const outlinedBorderDefault =
    typeof outlinedBorderColor === 'object'
      ? outlinedBorderColor?.default
      : outlinedBorderColor;
  const outlinedBorderPressed =
    typeof outlinedBorderColor === 'object'
      ? outlinedBorderColor?.pressed
      : undefined;

  const outlined = {
    ...button?.outlined,
    'border-color': outlinedBorderDefault,
    'text-color': button?.outlined?.['text-color'],
    icon: {
      fill: button?.outlined?.icon?.fill,
    },
    hover: {
      icon: { fill: semantic?.['icon-color']?.control?.hover },
    },
    pressed: {
      'border-color': outlinedBorderPressed,
      icon: {
        fill: semantic?.['icon-color']?.control?.pressed,
      },
    },
    focus: {
      icon: {
        fill: semantic?.['icon-color']?.control?.pressed,
      },
    },
    active: {
      icon: { fill: semantic?.['icon-color']?.control?.active },
    },
    selected: {
      'background-color': semantic?.['background-color']?.selected?.default,
      'border-color': semantic?.['border-color']?.selected,
    },
    disabled: {
      'border-color': outlinedBorderDefault,
      'text-color': button?.outlined?.['text-color'],
      icon: {
        fill: button?.outlined?.icon?.fill,
      },
    },
    loading: {
      'border-color': outlinedBorderDefault,
      'text-color': button?.outlined?.['text-color'],
    },
    alert: {
      'text-color': semantic?.['status-color']?.error,
      icon: { fill: semantic?.['status-color']?.error },
    },
  };

  const flat = {
    ...button?.flat,
    'border-color': button?.flat?.['border-color'],
    'text-color': button?.flat?.['text-color'],
    icon: {
      fill: button?.flat?.icon?.fill,
    },
    hover: {
      'background-color': 'transparent',
      'text-color': semantic?.['text-color']?.medium?.default,
      icon: {
        fill: semantic?.['icon-color']?.control?.hover,
      },
    },
    pressed: {
      'background-color': 'transparent',
      'text-color': semantic?.['text-color']?.medium?.default,
    },
    selected: {
      'background-color': button?.segmented?.['background-color']?.pressed,
    },
    disabled: {
      'text-color': button?.flat?.['text-color'],
      icon: {
        fill: button?.flat?.icon?.fill,
      },
    },
    loading: {
      'text-color': button?.flat?.['text-color'],
    },
    alert: {
      'text-color': semantic?.['status-color']?.error,
      icon: {
        fill: semantic?.['status-color']?.error,
      },
    },
  };

  const buttonSize = {
    height: button?.height,
    'font-size': button?.['font-size'],
    'line-height': button?.['line-height'],
    'padding-text': `0 ${generic?.spacing?.s}`,
    'padding-icon': `0 ${generic?.spacing?.xs}`,
    'padding-icon-text': `0 ${generic?.spacing?.s} 0 ${generic?.spacing?.xs}`,
    'icon-size': Number.parseInt(button?.icon?.width || '16', 10),
  };

  const smallButtonSize = highDensityTokens
    ? {
        height: highButton?.height,
        'font-size': highButton?.['font-size'],
        'line-height': highButton?.['line-height'],
        'padding-text': `0 ${highGeneric?.spacing?.s}`,
        'padding-icon': `0 ${highGeneric?.spacing?.xs}`,
        'padding-icon-text': `0 ${highGeneric?.spacing?.s} 0 ${highGeneric?.spacing?.xs}`,
        'icon-size': Number.parseInt(highButton?.icon?.width || '16', 10),
      }
    : SMALL_BUTTON_FALLBACK;

  const buildBoxShadow = (state: 'focus' | 'hover' | 'pressed') =>
    `${button?.['box-shadow-x']?.[state]}px ${button?.['box-shadow-y']?.[state]}px ${button?.['box-shadow-blur']?.[state]}px ${button?.['box-shadow-spread']?.[state]}px ${button?.['box-shadow-color']?.[state]}`;

  return {
    ...button,
    'font-family': fontFamily,
    'border-radius': button?.['border-radius'],
    'border-width': button?.['border-width'],
    'font-weight': button?.['font-weight'],
    solid,
    outlined,
    flat,
    small: smallButtonSize,
    medium: buttonSize,
    'box-shadow': {
      focus: buildBoxShadow('focus'),
      hover: buildBoxShadow('hover'),
      pressed: buildBoxShadow('pressed'),
    },
    'icon-margin':
      button?.['icon-left']?.['icon-container']?.['margin-right'] ??
      generic?.spacing?.xs,
    opacity: {
      disabled: button?.opacity?.disabled,
    },
    link: {
      small: {
        fontSize: highDensityTokens
          ? highButton?.['font-size']
          : button?.['font-size'],
        lineHeight: highDensityTokens
          ? highButton?.['line-height']
          : button?.['line-height'],
        fontFamily,
      },
      medium: {
        fontSize: button?.['font-size'],
        lineHeight: button?.['line-height'],
        fontFamily,
      },
    },
  };
};
